import { ColumnAggregationFunctions } from "@multiweb/aggregationRow";
import { StandardOperationRights } from "Global/Base/Rights/StandardOperationRights";
import { IOrderBy } from "../../../../Global/Base/DataSources/Interfaces";
import { Event, StatefulEvent } from "../../../../Global/Base/Events";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { IInteractive } from "../../../CommonUIInterfaces";
import { ComponentTreeType } from "../../LogicalTree/Definitions";
import { RightsManagerType } from "../../Rights/Definitions";
import { IMenu } from "../FormUI/FormUI";
import { FrameContext } from "../UIManagerInterfaces";
import { IFilterManager } from "./Filters/IFilterManager";
import { IFormControl } from "./FormControl";
import { HideButtons, IActualObjectChangedEventArgs } from "./Frames";
import { IMasterableBase } from "./Frames/FrameInterfaces";

/**
 * Rozhraní pro obecnou implementaci framu
 * @includeToDoc
 * @generateInstanceClass
 */
export interface IFrame extends IFormControl, IInteractive, IMasterableBase {
    /**
     * Unikátní idenfikátor framu. Musí být jedinečný v aplikaci, nehledě na otevřeném formuláři nebo počtu jeho instancí. Používá se jako klíč do reduxu
     */
    uuid: string;
    /**
     * Zda je frame hlavním framem
     */
    isMainFrame: boolean;
    /**
     * Element představující vizuální frame
     */
    element: HTMLElement;
    /**
     * Lokalizační klíč určujíci název (titulek) framu
     */
    locKey: string;
    /**
     * Nazev framu, musí být unikatni na formuláři, slouží k provázani framu
     */
    frameName: string;

    /**
     * Právě vybrany/zobrazeny doménový objekt ve framu
     */
    actualObject: OWObject;
    /**
     * Kontext framu
     */
    readonly frameContext: FrameContext;

    /**
     * Zda se ma do seznamu pridavat 3dot akce
     */
    renderStandardRowActions: boolean;
    /**
     * Událost vyvolaná při změně hodnoty v akutálním objektu
     */
    readonly actualObjectValueChanged: Event<IFrame, OWObject>;

    /**
     * Zda lze s framemem vůbec něják pracovat
     */
    enabled: boolean;
    /**
     * Nastavení viditelnosti
     */
    visible: boolean;

    /**
     * Zda je frame aktivni a lze s nim pracovat a pouštět nad nim akce (musí být povolen právy a nesmí být ve stavu jen ke čteni)
     */
    isActive: boolean;

    /**
     * Skryte akce
     */
    hideButtons: HideButtons;

    /**
     * Po zaktivnění formu dojde k refreshi aktuálního objektu
     */
    refreshObjectAfterSetActive: boolean;

    /**
     * Po zaktivnění formu dojde k refreshi aktuálního framu
     */
    refreshListAfterSetActive: boolean;

    /**
     * Událost oznamujíci zviditelneni framu
     */
    frameIsVisible: Event<IFrame, void>;

    /**
     * Událost vyvolaná při otevírání kontextového menu ve framu
     */
    contextMenuOpening: Event<IFrame, IFrameCtxMenuData>;

    /**
     * Událost, která nastane při změna aktuálně vybranného objektu.
     */
    readonly actualObjectChanged: Event<IFrame, IActualObjectChangedEventArgs>;

    /**
     * Událost, která nastave v momentě kdy frame má připravné akce pro aktuálně vybraný objekt
     */
    readonly contextActionsReady: Event<IFrame, string>;

    /**
     * Událost vyvolaná při dokončení inicializace framu
     */
    readonly initialized: StatefulEvent<IFrame, void>;

    /**
     * Příznak, zda již byla spuštěna inicializace framu
     */
    readonly initializationStarted: boolean;

    /**
     * Příznak, zda již byl frame inicializován
     */
    readonly isInitialized: boolean;

    /**
     * Informace o tom, zda má byt skrytý toolbar
     */
    hideToolBar: boolean;

    /**
     * Toolbar
     */
    menu: IMenu;

    /**
     * Znicializuje frame a jeho nezbytné součásti.
     * Inicializace jeho vizuálních součástí se dělá lazy až po jeho prvním zobrazení.
     * Musí být zavoláno před jeho prvním použitím.
     */
     initialize(): Promise<void>;
    /**
    * Obsahuje master object
    */
    masterObject: OWObject;

    /**
     * Název nadřízeného framu, musí se shodovat s frameName jiného framu nebo bude prázdny
     */
    masterFrameName: string | null;

    /**
     * Název vazby na nadřizeny objekt
     */
    masterLinkToAttr: string | null;
    /**
     * Název vazby z nadřizeného objektu, pokud neni vyplnen pouzije se původně vybraný objekt
     */
    masterAttr: string | null;

    /**
     * Znovunačtení všech zobrazených dat
     * @param manualRefresh znači zda je akce vyvolana uživatelem (zabraňuje nebo povoluje označeni řádku v seznamu)
     */
    refreshData(manualRefresh: boolean): Promise<void>;
    /**
     * Refreshne právě vybraný domenový objekt
     */
    refreshActualObject(): Promise<void>;
    /**
     * Čeká na dočtení dat
     */
    waitForDataLoad(): Promise<boolean>;
    /**
     * Čeká na doběhnutí změn dat
     */
    waitForPendingUpdates(): Promise<void>;
    /**
     * Počká na dokončení probíhajícího nastavení ActualObjectu
     */
    waitForActualObjectSet(): Promise<boolean>;

    /**
     * Potlačuje zobrazení YesNoCancel dialogu
     */
    readonly suppressSaveDialog: boolean;

    /**
     * Příznak že na framu probíhá načítání objektu
     */
    readonly isSetActualObjectRunning?: boolean;
}

export class IFrame {
    private static _properties: (keyof IFrame)[] = ["isMainFrame", "enabled", "visible", "isActive", "masterLinkToAttr", "masterAttr", "frameName", "masterFrameName", "masterObject", "masterFrame", "actualObject", "actualObjectChanged", "actualObjectValueChanged", "element", "locKey", "refreshObjectAfterSetActive", "frameIsVisible", "contextMenuOpening", "contextActionsReady", "initialized", "isInitialized", "suppressSaveDialog"];
    private static _methods: (keyof IFrame)[] = ["refreshData", "refreshActualObject", "waitForDataLoad", "waitForPendingUpdates", "waitForActualObjectSet", "initialize"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function") &&
            instance instanceof IInteractive &&
            instance instanceof IMasterableBase;
    }
}

/**
 * Frame, u kterého lze vyhodnocovat práva
 */
export interface IFrameWithRights extends IFrame {
    //TODO: přesunout readonly sem

    /**
     * Příznak, že má frame vybraný záznam
     */
    hasSelectedRecord: boolean;

    /**
     * Právo vkládání na formulářové úrovni z formuláře
     */
    canInsert: boolean | undefined | null;

    /**
     * Právo mazání
     */
    canDelete: boolean | undefined | null;
    /**
     * Výsledek tvrdého práva čtení na formulářové úrovni (podmínka se uplatňuje do podmínky čtení hlavního seznamu)
     */
    formCanRead: boolean;

    /**
     * TODO-rites
     */
    readonly actualObjectStandardRights: StandardOperationRights;

     /**
     * TODO-rites
     */
    componentTree: ComponentTreeType;
    /**
     * TODO-rites
     */
    readonly rightsManager: RightsManagerType;
}

export class IFrameWithRights {
    private static _properties: (keyof IFrameWithRights)[] = ["canInsert", "hasSelectedRecord", "formCanRead", "componentTree", "actualObjectStandardRights", "rightsManager", "suppressSaveDialog"];
    private static _methods: (keyof(IFrameWithRights))[] = [];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function") &&
            instance instanceof IFrame;
    }
}

/**
 * Reprezentuje rozšířená data s informacemi o kontextu a události otevření kontextového menu
 */
export interface IFrameCtxMenuData {
    /**
     * Identifikátor datového záznamu nebo null, pokud se událost nevztahuje k žádnému
     */
    dataKey: string | null;
    /**
     * Původní událost otevírání kontextového menu
     */
    originalEvent: MouseEvent;
}

/**
 * Reprezentuje seznam (tabulku se sloupčky a řádky)
 * @includeToDoc
 * @generateInstanceClass
 */
export interface IListFrame extends IFrame {
    /**
     * Řazení seznamu
     */
    readonly orderBy: IOrderBy[];
    /** Určuje, zda je povolen tisk */
    readonly allowReport: boolean;
    readonly filterManager: IFilterManager;
    /**
     * Reflektuje XGenovou vlastnost z definice formuláře
     */
    isEditable?: boolean;
    /**
     * Zjištění, zda by se měl posílat dotaz na vyhodnocení InsertRight
     */
    hasInsertControl?: boolean;
}

export class IListFrame {
    private static _properties: (keyof IListFrame)[] = ["orderBy", "allowReport", "filterManager"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) && instance instanceof IFrame;
    }
}

/**
 * Rozhraní seznamu s podporou agregačního řádku.
 */
export interface IAggregableListFrame {
    /**
     * Získá objekt obsahující zvolené agregační funkce pod klíči sloupců agregačního řádku.
     * V případě že je agregační řádek skrytý vyhodí výjimku.
     */
    getColumnsAggregationFunctions(): ColumnAggregationFunctions;
    /**
     * Příznak zda je viditelný agregační řádek.
     */
    isAggregationRowVisible(): boolean;
    /**
     * Příznak zda byla zvolena alespoň jedna agregační funkce
     */
    hasSelectedAggregationFunctions(): boolean;
}

export class IAggregableListFrame {
    private static _methods: (keyof IAggregableListFrame)[] = ["getColumnsAggregationFunctions", "isAggregationRowVisible", "hasSelectedAggregationFunctions"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && this._methods.every((value) => Reflect.has(instance, value));
    }
}
